import { useCallback, useMemo, useRef } from 'react';
import { SubscriptionType } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/subscription_backfill_enums.pb';
import { getWeaveSessionId } from '@frontend/auth-helpers';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { useComponentEngagementShallowStore, useComponentEngagementStore } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useMatch } from '@frontend/weave-router';
import { CALL_RECORDING_SETTINGS } from '../../constants';
import { useBackfillStatus } from '../../hooks/use-backfill-status';
import { useCallRecordingEnabled } from '../../hooks/use-call-recording-enabled';
import { backfillBannerPendoEvents, backfillBannerTrackingIds, trackBackfillBannerEvent } from '../../tracking-ids';
import { RecordingsDisabledBannerScenario } from '../../types';

const COMPONENT_ID = 'backfill-call-recordings-disabled-global-banner';

export const BackfillCallRecordingsDisabledGlobalBanner = () => {
  const { t } = useTranslation('call-intelligence');
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const isCallIntelligencePage = !!useMatch({ from: '/analytics/call-intelligence', shouldThrow: false });

  const { locationStatuses, isLoading: isBackfillStatusLoading, isMixedSubscriptionPlan } = useBackfillStatus();
  const locationIds = useMemo(() => locationStatuses.map((loc) => loc.locationId), [locationStatuses]);

  const { isRecordingSettingsLoading, hasCallRecordingWriteAccess, hasAnyRecordingsDisabled } =
    useCallRecordingEnabled(locationIds);

  const isLoading = isBackfillStatusLoading || isRecordingSettingsLoading;

  const { trackDismiss } = useComponentEngagementShallowStore('trackDismiss');
  const engagementData = useComponentEngagementStore((state) => state.getEngagementData({ componentId: COMPONENT_ID }));

  const isVisible = useMemo(() => {
    if (isLoading || locationStatuses.length === 0 || isCallIntelligencePage) {
      return false;
    }

    if (!hasCallRecordingWriteAccess) {
      return false;
    }

    // Dismissal rules: The banner will be dismissed for that session only.
    // Use the session id from getWeaveSessionId and use the component-engagement-store to manage its state.
    const currentSessionId = getWeaveSessionId();
    if (currentSessionId && engagementData.dismissedSessionId === currentSessionId) {
      return false;
    }

    return hasAnyRecordingsDisabled;
  }, [
    isLoading,
    locationStatuses,
    isCallIntelligencePage,
    hasCallRecordingWriteAccess,
    engagementData,
    hasAnyRecordingsDisabled,
  ]);

  const handleDismiss = useCallback(() => {
    const sessionId = getWeaveSessionId();
    if (sessionId) {
      trackDismiss({ componentId: COMPONENT_ID, sessionId });
    }
  }, [trackDismiss]);

  const action = useMemo(
    () => ({
      label: t('Enable Call Recordings'),
      onClick: () => {
        settingsNavigate({ to: CALL_RECORDING_SETTINGS });
      },
    }),
    [settingsNavigate, t]
  );

  const hasFiredPendoRef = useRef(false);

  const bannerConfig = useMemo(() => {
    if (!isVisible) return null;

    // Check if we have locations with TRIAL
    const hasTrial = locationStatuses.some((loc) => loc.subscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_TRIAL);
    // Check if we have locations with BASE or PRO
    const hasBaseOrPro = locationStatuses.some(
      (loc) =>
        loc.subscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_BASE ||
        loc.subscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_PRO
    );

    const isAllTrialIncomplete =
      hasTrial &&
      !hasBaseOrPro &&
      locationStatuses.every(
        (loc) => loc.subscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_TRIAL && loc.isBackfillIncomplete
      );

    const isAllSubscriptionIncomplete =
      hasBaseOrPro &&
      !hasTrial &&
      locationStatuses.every(
        (loc) =>
          (loc.subscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_BASE ||
            loc.subscriptionType === SubscriptionType.SUBSCRIPTION_TYPE_PRO) &&
          loc.isBackfillIncomplete
      );

    if (isAllTrialIncomplete && !isMixedSubscriptionPlan) {
      return {
        scenario: 'trial' as RecordingsDisabledBannerScenario,
        title: t('Call Recordings are Disabled for Your Call Intelligence Free Trial'),
        message: t(
          'We are unable to analyze any new calls for your free trial. Please enable call recordings so that Call Intelligence can transcribe and analyze new calls and prepare your dashboard.'
        ),
      };
    }

    if (isAllSubscriptionIncomplete && !isMixedSubscriptionPlan) {
      return {
        scenario: 'subscription' as RecordingsDisabledBannerScenario,
        title: t('Call Recordings are Disabled for Your Call Intelligence Subscription'),
        message: t(
          'We are unable to analyze any new calls for your subscription. Please enable call recordings so that Call Intelligence can transcribe and analyze new calls and prepare your dashboard.'
        ),
      };
    }

    return {
      scenario: 'generic' as RecordingsDisabledBannerScenario,
      title: t('Call Recordings are Disabled for Call Intelligence'),
      message: t(
        'Calls are not being transcribed or analyzed by Call Intelligence. Enable Call Recordings to resume call transcription and analysis.'
      ),
    };
  }, [isVisible, locationStatuses, isMixedSubscriptionPlan, t]);

  const handleVisible = useCallback(() => {
    if (!hasFiredPendoRef.current && bannerConfig) {
      hasFiredPendoRef.current = true;
      trackBackfillBannerEvent(backfillBannerPendoEvents.recordingsDisabledBannerSeen, {
        scenario: bannerConfig.scenario,
      });
    }
  }, [bannerConfig]);

  if (!bannerConfig) return null;

  return (
    <GlobalBannerDynamic
      type='error'
      surfaceId='banner.call-intel-backfill'
      id='call-intel-backfill-recordings-disabled-global'
      title={bannerConfig.title}
      message={bannerConfig.message}
      isDismissible
      onDismiss={handleDismiss}
      onVisible={handleVisible}
      action={action}
      trackingId={backfillBannerTrackingIds.recordingsDisabledBannerCta}
      dismissBtnTrackingId={backfillBannerTrackingIds.recordingsDisabledBannerDismiss}
    />
  );
};
